import SEO from '../components/SEO';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, TruckIcon, CreditCard, Warehouse, Package, BarChart3, Zap, Shield, Clock, Target, Users, Settings, AlertCircle, ArrowRight, Check, Calendar, Store, Building2, TrendingUp, ShoppingCart, Globe, DollarSign, Banknote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const TimberMerchantsLanding = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', branches: '', consent: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/forms/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: `Branches: ${formData.branches}`,
        source: 'Timber Merchants Landing Page'
      });
      setSubmitted(true);
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO 
        title="SAP Business One for Timber Merchants | Timber & Builders Supplies ERP"
        description="Handle timber grades (C24/C16), HIAB delivery, price volatility, trade accounts. Complete integrated platform for timber merchants and sawmills."
        keywords="timber merchant ERP, sawmill software, timber grade tracking, builders supplies ERP, SAP B1 timber"
        canonical="https://www.ascarii.com/landing/timber-merchants"
      />

      {/* Hero Section - Centered Glass Panel Style */}
      <section className="relative min-h-[700px] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img src="/timber-merchant-hero.jpeg" alt="Timber Merchant" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-950/70 to-slate-900/80" />
        </div>
        
        {/* Centered Content */}
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-sm font-semibold mb-6 text-white">For UK Timber Merchants</div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Integrated Platform for Timber Merchants
              </h1>
              
              <p className="text-xl md:text-2xl text-sky-300 mb-8 font-medium max-w-3xl mx-auto">
                Control margin, stock, and cash across bulky timber lines. Real-time yard visibility. Faster counter service. HIAB delivery with ePOD.
              </p>
              
              {/* Glass Panel with Key Features */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 mb-8 max-w-3xl mx-auto">
                <p className="text-white font-semibold mb-6 text-lg">Built for timber merchant operations</p>
                <div className="grid md:grid-cols-2 gap-4 text-left">
                  {[
                    { icon: CheckCircle2, text: "Real-time stock across yards, warehouses, and branches" },
                    { icon: CheckCircle2, text: "Margin control for sheet materials, C24/C16, mouldings" },
                    { icon: CheckCircle2, text: "Purchasing discipline for price volatility" },
                    { icon: CheckCircle2, text: "HIAB/crane delivery with electronic POD" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <item.icon className="w-5 h-5 text-cyan-300 flex-shrink-0 mt-1" />
                      <span className="text-white text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-4 mb-6">
                <a href="#demo">
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white px-10 py-5 text-lg font-semibold shadow-xl">
                    <Calendar className="w-5 h-5 mr-2" />
                    Book a Demo
                  </Button>
                </a>
              </div>

              <p className="text-blue-200 text-sm">
                See how timber merchants control margins and eliminate delivery disputes
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Connected Stack */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0\" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">The Connected Timber Merchant Stack</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">Five best-in-class systems working as one</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-8 max-w-5xl mx-auto">
            {[
              { icon: BarChart3, product: "SAP Business One", role: "Core ERP", desc: "Single source of truth for products, pricing, customers, purchasing, batch tracking, bin locations, and UK VAT/MTD compliance.", color: "from-blue-500 to-sky-500", link: "/sap-business-one" },
              { icon: CreditCard, product: "EPoS Now", role: "Counter POS", desc: "Fast cloud POS for busy trade counters. Syncs products, customers, and trade pricing. Pushes sales back to ERP automatically.", color: "from-sky-500 to-cyan-500", link: "/products/epos-now" },
              { icon: ShoppingCart, product: "FocusPoint", role: "B2B E-Commerce", desc: "Trade portal built for SAP Business One. Customer-specific pricing, click-&-collect time windows, quote-to-order, and real-time stock across branches.", color: "from-slate-800 to-blue-900", link: "/products/focuspoint" },
              { icon: Warehouse, product: "Produmex WMS/Scan", role: "Yard Execution", desc: "Barcode-driven receiving, putaway to bins, picking with batch control. Choose Scan (light) or WMS (advanced) as you scale.", color: "from-purple-500 to-pink-500", link: "/products/produmex-wms" }
            ].map((item, i) => (
              <a key={i} href={item.link} target="_blank" rel="noopener noreferrer">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all cursor-pointer h-full">
                  <div className="flex items-start gap-6">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0`}>
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-1">{item.product}</h3>
                      <p className="text-sm text-cyan-300 font-medium mb-2">{item.role}</p>
                      <p className="text-blue-100 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </motion.div>
              </a>
            ))}
          </div>
          
          <div className="flex justify-center max-w-5xl mx-auto mb-20">
            <a href="/products/stream" target="_blank" rel="noopener noreferrer" className="w-full md:w-1/2">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.4 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all cursor-pointer h-full">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center flex-shrink-0">
                    <TruckIcon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">Stream</h3>
                    <p className="text-sm text-cyan-300 font-medium mb-2">Transport & ePOD</p>
                    <p className="text-blue-100 text-sm leading-relaxed">Route planning for multi-drop deliveries, live tracking, and electronic proof of delivery. Two-way sync with SAP Business One for watertight records.</p>
                  </div>
                </div>
              </motion.div>
            </a>
          </div>

          {/* How It Works */}
          <div className="border-t border-white/20 pt-20 mt-8" id="how-it-works">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works: Web to ePOD</h2>
              <p className="text-xl text-blue-100">Five steps to complete operational visibility</p>
            </div>
            <div className="flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-2">
              {[
                { step: "1", title: "Web Order", kpi: "Real-time stock", desc: "FocusPoint pulls live stock and pricing from SAP Business One. Customer places order with click-&-collect time window." },
                { step: "2", title: "Counter Sale", kpi: "Trade pricing instant", desc: "EPoS Now shows trade prices and account details. Staff process sale. Transaction posts to SAP Business One automatically." },
                { step: "3", title: "Yard Pick", kpi: "<3 min pick time", desc: "Barcode picking with bin locations. WMS directs optimal path through yard. Batch/serial captured for compliance." },
                { step: "4", title: "Route Plan", kpi: "Optimised routes", desc: "Stream plans multi-drop runs to building sites. Drivers get turn-by-turn navigation and delivery sequence." },
                { step: "5", title: "ePOD & Sync", kpi: "Zero disputes", desc: "Driver captures photo/signature. Stream posts delivery status and POD back to SAP Business One for invoicing." }
              ].map((flow, i) => (
                <React.Fragment key={i}>
                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} viewport={{ once: true }} className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all text-center w-full md:flex-1 md:max-w-[200px] h-full flex flex-col">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 text-white flex items-center justify-center font-bold text-xl mb-4 mx-auto flex-shrink-0">{flow.step}</div>
                    <h3 className="font-bold text-white mb-2 text-sm">{flow.title}</h3>
                    <div className="inline-block px-2 py-1 bg-cyan-400/20 text-cyan-300 rounded text-xs font-semibold mb-2">{flow.kpi}</div>
                    <p className="text-blue-100 text-xs leading-relaxed flex-1">{flow.desc}</p>
                  </motion.div>
                  {i < 4 && (
                    <ArrowRight className="hidden md:block w-6 h-6 text-cyan-300 flex-shrink-0 self-center" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Pain */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">The Reality for Most Timber Merchants</h2>
          </div>
          
          <div className="space-y-6">
            {[
              { title: "Slow Counter Service & Pricing Chaos", desc: "Staff hunting for stock across systems. Trade pricing inconsistent between branches. Promotions not applied. Long queues. Builders frustrated." },
              { title: "Lost Yard Tickets & Mispicks", desc: "Paper tickets going missing. No real-time bin visibility. Substitutes picked without customer approval. Returns and complaints mounting." },
              { title: "Poor Stock Visibility", desc: "Can't see what's in other branches or yards. Click-&-collect promises broken. Customers turning up for items that aren't there." },
              { title: "Paper PODs & Delivery Disputes", desc: "Van sheets lost or illegible. No proof of delivery. Customers claiming non-delivery. Invoice disputes blocking cash flow." },
              { title: "Double Entry & Compliance Risk", desc: "Keying counter sales into system. Updating inventory manually. Batch tracking patchy. UK VAT/MTD records scattered. HMRC audit exposure." }
            ].map((pain, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="flex items-start gap-4 p-6 bg-white rounded-xl border-l-4 border-red-600 shadow-md">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{pain.title}</h3>
                  <p className="text-gray-600 text-sm">{pain.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">There's a Better Way</h3>
            <p className="text-xl text-blue-100">One connected stack eliminates these pain points. Web, counter, yard, and delivery working as one.</p>
          </div>
        </div>
      </section>

      {/* SAP Business One Core Benefits for Timber Merchants */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why SAP Business One for Timber Merchants</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Built for high-volume, margin-sensitive timber distribution with complex inventory and frequent supplier price movements</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              { 
                icon: Target, 
                title: "Better Margin and Stock Control", 
                desc: "Manage high-volume, margin-sensitive lines: sheet materials, C24/C16, treated/untreated, mouldings, fixings. Full order-to-pay cycle control protects cost accuracy and margin. Warehouse and accounting integration synchronises goods receipts and inventory levels in real-time. Eliminate 'system says yes, yard says no' situations."
              },
              { 
                icon: Warehouse, 
                title: "Advanced Inventory for Bulky Materials", 
                desc: "Multi-warehouse management designed for timber products. Real-time stock control across yards and branches. Handle mixed baskets (boards, sheet, fixings) efficiently. Optimise working capital without risking stockouts on fast-moving lines."
              },
              { 
                icon: TrendingUp, 
                title: "Purchasing Discipline for Price Volatility", 
                desc: "Tight purchasing and inventory controls crucial when supplier prices change frequently. End-to-end order-to-pay: receipts, invoices, returns, payments. Manage rebates, volume breaks, and cost fluctuations. Protect margins in a competitive market."
              },
              { 
                icon: BarChart3, 
                title: "Financial Visibility Across Branches", 
                desc: "Real-time financial reporting and analysis. Clear visibility over margins, COGS, and profitability by product line, branch, or customer. Branch-level P&L while maintaining group-wide consistency. Make data-driven decisions on stock, pricing, and purchasing."
              }
            ].map((benefit, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.4, delay: i * 0.1 }} 
                viewport={{ once: true }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{benefit.title}</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ascarii Go Implementation Benefits */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Ascarii's Delivery Model Matters</h2>
            <p className="text-xl text-gray-600">How you implement determines your success</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Clock,
                title: "Faster Time to Value",
                desc: "SAP Business One core (finance, stock, sales) implemented with our templated AscariiGo approach. Pilot at one branch, prove it works, then roll out to remaining locations in controlled phases."
              },
              {
                icon: Banknote,
                title: "Predictable, Scalable Pricing",
                desc: "Low monthly subscription that scales as you grow. No massive upfront infrastructure projects. Easier to justify than large capital expenditure."
              },
              {
                icon: Shield,
                title: "Unlimited Support Included",
                desc: "Round-the-clock support from specialist consultants included in your AscariiGo subscription. Service desk for tickets and knowledge resources. When downtime hits counter trade, we respond immediately."
              },
              {
                icon: Zap,
                title: "Cloud-First Implementation",
                desc: "AWS-hosted SAP Business One with 99.995% uptime. Lower IT overhead. Easier integrations for couriers, eCommerce, and scanning without big infrastructure projects."
              },
              {
                icon: Settings,
                title: "Easier System Integration",
                desc: "Practical integration support for courier tracking, CRM, handheld scanning, and barcode/label printing. Hosting environment supports complementary apps alongside SAP Business One."
              },
              {
                icon: Target,
                title: "Clear Discovery & Design",
                desc: "Transparent pricing and timescales from day one. Avoid unexpected costs and scope creep. Proven implementation approach designed for SME merchants."
              }
            ].map((benefit, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.4, delay: i * 0.1 }} 
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{benefit.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white text-center"
          >
            <h3 className="text-2xl font-bold mb-3">Day-to-Day Impact</h3>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto text-left mt-6">
              {[
                { icon: Users, text: "Counter staff trust stock availability by branch/yard - reducing promise breaks" },
                { icon: Package, text: "Purchasing reacts faster to demand and supplier changes with fewer manual workarounds" },
                { icon: BarChart3, text: "Finance gets cleaner, faster visibility on margin, cash, and branch performance" },
                { icon: Zap, text: "IT overhead drops while integrations (couriers/ecom/scanning) become more achievable" }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <item.icon className="w-5 h-5 text-cyan-300 flex-shrink-0 mt-0.5" />
                  <span className="text-blue-50 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Proven Outcomes for Timber Merchants</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { metric: "+12%", outcome: "Counter Throughput", desc: "Faster lookups and instant pricing" },
              { metric: "-30%", outcome: "Picking Errors", desc: "Barcode-driven accuracy with bins" },
              { metric: "-25%", outcome: "Failed Deliveries", desc: "Live ETAs and customer notifications" },
              { metric: "+15%", outcome: "Online Conversion", desc: "Real-time stock and pricing" },
              { metric: "100%", outcome: "POD Capture", desc: "Every delivery has photo/signature" },
              { metric: "Zero", outcome: "Double Entry", desc: "Automatic sync eliminates keying" }
            ].map((outcome, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-200 hover:shadow-lg transition-all text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{outcome.metric}</div>
                <h3 className="font-bold text-gray-900 mb-2">{outcome.outcome}</h3>
                <p className="text-gray-600 text-sm">{outcome.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FocusPoint B2B Portal */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch mb-16">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="/timber-merchant-focuspoint.jpeg" alt="Timber Merchant E-Commerce" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4 w-fit">B2B E-Commerce</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Trade Portal That Works 24/7</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                FocusPoint transforms SAP Business One into a powerful B2B portal for your trade accounts. Customer-specific pricing, real-time stock across all branches, click-&-collect time windows, and quote-to-order workflows - all from your SAP data.
              </p>
              <div className="space-y-3">
                {[
                  "Customer-specific pricing and volume breaks",
                  "Real-time stock across all branches and yards",
                  "Click-&-collect with time window selection",
                  "Quick-order lists for repeat trades",
                  "Quotes convert to orders in SAP Business One",
                  "Multi-branch delivery options"
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <a href="/products/focuspoint" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-blue-600 hover:bg-blue-700">Learn More About FocusPoint</Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features by Role */}
      <section className="py-20 relative overflow-hidden" style={{ 
        backgroundImage: 'linear-gradient(rgba(249, 250, 251, 0.95), rgba(249, 250, 251, 0.95)), url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Built for Every Role in Your Operation</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              { role: "Trade Counter & Branch", icon: Users, color: "from-blue-500 to-sky-500", features: ["Instant trade pricing with volume breaks", "Account sales with credit limits", "Customer purchase history", "Promotions applied automatically", "Stock check across all branches"] },
              { role: "E-Commerce & Click-&-Collect", icon: ShoppingCart, color: "from-slate-800 to-blue-900", features: ["Customer-specific pricing online", "Real-time stock all branches", "Click-&-collect time windows", "Quick-order lists for repeat trades", "Quote-to-order workflow"] },
              { role: "Yard & Warehouse", icon: Warehouse, color: "from-purple-500 to-pink-500", features: ["Barcode receiving with batch capture", "Directed putaway to optimal bins", "Pick-by-location for heavy materials", "Cycle counting without stocktake", "SSCC labelling for pallets"] },
              { role: "Transport & Drivers", icon: TruckIcon, color: "from-teal-600 to-cyan-600", features: ["Optimised multi-drop to sites", "Turn-by-turn navigation", "Live ETA updates to customers", "Photo/signature ePOD capture", "Failed delivery workflow"] },
              { role: "Finance & IT", icon: Settings, color: "from-indigo-500 to-violet-500", features: ["UK VAT/MTD compliant", "Batch/serial audit trail", "API-driven integrations", "Real-time financial visibility", "Single sign-on"] }
            ].map((group, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="bg-white rounded-xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all text-center">
                <div className="flex flex-col items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${group.color} flex items-center justify-center`}>
                    <group.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{group.role}</h3>
                </div>
                <ul className="space-y-2 inline-block text-left">
                  {group.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WMS vs Scan */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Choose Your Yard Approach</h2>
            <p className="text-xl text-gray-600">Right-sized for your operation</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              { product: "Produmex Scan", subtitle: "Lightweight Barcode Layer", best: "Best for: 1-3 branches, straightforward yard operations", features: ["Barcode receiving and putaway", "Pick-to-order with bin locations", "Batch/serial capture for compliance", "Cycle counting", "2-hour deployment"], color: "purple", link: "/products/produmex-scan" },
              { product: "Produmex WMS", subtitle: "Advanced Yard Management", best: "Best for: Multi-site, complex operations, heavy volumes", features: ["Directed putaway workflows", "Wave picking for bulk orders", "Packing with SSCC labels", "Route-based loading", "Multi-yard orchestration"], color: "blue", link: "/products/produmex-wms" }
            ].map((option, i) => (
              <a key={i} href={option.link} target="_blank" rel="noopener noreferrer">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.15 }} viewport={{ once: true }} className={`bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border-2 ${option.color === 'purple' ? 'border-purple-300' : 'border-blue-300'} hover:shadow-xl transition-all cursor-pointer h-full text-center`}>
                  <div className="flex flex-col items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-lg ${option.color === 'purple' ? 'bg-purple-600' : 'bg-blue-600'} flex items-center justify-center`}>
                      <Warehouse className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{option.product}</h3>
                      <p className="text-sm text-gray-600">{option.subtitle}</p>
                    </div>
                  </div>
                  <p className={`text-sm font-semibold ${option.color === 'purple' ? 'text-purple-700' : 'text-blue-700'} mb-4`}>{option.best}</p>
                  <ul className="space-y-2 inline-block text-left">
                    {option.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <Check className={`w-4 h-4 ${option.color === 'purple' ? 'text-purple-600' : 'text-blue-600'} flex-shrink-0 mt-0.5`} />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Perfect For */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Perfect for UK Timber Merchants</h2>
            <p className="text-xl text-gray-600">Whether you're single-site or multi-branch</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { icon: Store, scenario: "Single-site merchants with trade counter, yard, and local delivery" },
              { icon: Building2, scenario: "Multi-branch operations needing consistent pricing and stock visibility" },
              { icon: TrendingUp, scenario: "Growing merchants adding branches and scaling yard operations" },
              { icon: BarChart3, scenario: "Established merchants with 10+ branches requiring group reporting" },
              { icon: ShoppingCart, scenario: "Operations offering click-&-collect and trade account e-commerce" },
              { icon: Shield, scenario: "Merchants needing UK VAT/MTD compliance with batch/serial traceability" }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="flex items-start gap-4 p-6 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-gray-700 text-sm flex-1">{item.scenario}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Flow */}
      <section className="py-20 relative overflow-hidden" style={{ 
        backgroundImage: 'linear-gradient(rgba(249, 250, 251, 0.97), rgba(249, 250, 251, 0.97)), url(https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Real-Time Data Flow</h2>
            <p className="text-xl text-gray-600">Everything stays in sync automatically</p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 border-2 border-blue-200 shadow-lg">
            <div className="space-y-6">
              {[
                { from: "SAP Business One → FocusPoint & EPoS Now", sync: "Products, customers, pricing (incl. trade prices, customer-specific rates, volume breaks)" },
                { from: "FocusPoint → SAP Business One", sync: "Web orders, quotes, click-&-collect bookings written directly into SAP Business One" },
                { from: "EPoS Now → SAP Business One", sync: "Counter sales, payments, account transactions" },
                { from: "SAP Business One → Stream", sync: "Sales orders for delivery, customer details, delivery addresses" },
                { from: "Stream → SAP Business One", sync: "Delivery status, ePOD (photos/signatures), failed deliveries" },
                { from: "Produmex → SAP Business One", sync: "Goods receipts, picks, inventory adjustments, batch movements, bin updates" },
                { from: "SAP Business One → All Systems", sync: "Real-time inventory levels, bin locations, price updates across web, counter, and warehouse" }
              ].map((flow, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <ArrowRight className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-gray-900 mb-1">{flow.from}</p>
                    <p className="text-gray-600 text-sm">{flow.sync}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Common Questions from Merchant MDs</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: "How does the e-commerce integration work?", a: "FocusPoint is built exclusively for SAP Business One with no middleware. Customer orders and quotes are written directly into SAP Business One in real-time. Products, prices, and stock levels sync automatically from your ERP - one source of truth." },
              { q: "Can it handle customer-specific pricing and click-&-collect?", a: "Yes. SAP Business One's price lists, special prices, and volume breaks flow to both the web portal and counter POS. Click-&-collect time windows are managed in FocusPoint with availability checking across all branches. Customers pick their collection time and branch." },
              { q: "WMS or Scan - which do we need?", a: "Start with Produmex Scan for quick barcode wins if you're doing straightforward yard operations. Step up to Produmex WMS when you need directed workflows, wave picking for bulk orders, packing/SSCC labels, or managing inventory across multiple yards and branches." },
              { q: "Is electronic POD legally valid in the UK?", a: "Yes. Electronic signatures and photos captured via Stream meet UK legal requirements for proof of delivery. Timestamped GPS, photos, and signatures sync to SAP Business One creating an indisputable audit trail for invoicing and disputes." },
              { q: "What about UK VAT and Making Tax Digital?", a: "SAP Business One is MTD-ready with digital VAT submissions to HMRC. All sales (web, counter, and delivery) flow through SAP Business One, maintaining compliant digital records. Batch/serial tracking provides full traceability for hazardous materials compliance." },
              { q: "Can it work across multiple branches and yards?", a: "Absolutely. SAP Business One handles multi-branch with branch transfers, centralised purchasing, and group reporting. FocusPoint shows stock across all locations. EPoS Now supports multi-store. Stream manages deliveries from any depot. Produmex WMS orchestrates across sites." },
              { q: "How long does implementation take?", a: "Typical timber merchant goes live in 12-16 weeks: SAP Business One core first, then layer on POS, e-commerce, warehouse, and delivery. We pilot at one branch, prove it works, then roll out to remaining locations." },
              { q: "What about data migration from our current system?", a: "We extract your products, customers, pricing, and stock from your existing system and migrate to SAP Business One. This includes product hierarchies, customer trade accounts, and historical transactions. Migration is completed and validated during implementation." }
            ].map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-white p-6 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Form */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100" id="demo">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">See the Stack in Action</h2>
              <p className="text-xl text-gray-600 mb-8">Book a tailored demo for your timber merchant operation</p>
              
              <div className="space-y-6">
                {[
                  { icon: Shield, title: "SAP Business One Premier Partner", desc: "15+ years implementing for UK merchants" },
                  { icon: Target, title: "Timber Merchant Specialists", desc: "Proven implementations for trade operations" },
                  { icon: Zap, title: "Proven Stack", desc: "All five systems tested and integrated" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              {submitted ? (
                <div className="bg-white rounded-2xl p-12 border-2 border-green-200 shadow-xl text-center">
                  <CheckCircle2 className="w-20 h-20 mx-auto mb-6 text-green-600" />
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
                  <p className="text-gray-600">We'll be in touch within one business day to arrange your demo.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Book Your Demo</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                      <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Work Email *</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Company *</label>
                      <input type="text" required value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                      <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Branches *</label>
                      <Select required value={formData.branches} onValueChange={(value) => setFormData({...formData, branches: value})}>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select number of branches..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 branch</SelectItem>
                          <SelectItem value="2-5">2-5 branches</SelectItem>
                          <SelectItem value="6-10">6-10 branches</SelectItem>
                          <SelectItem value="10+">10+ branches</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="flex items-start gap-3">
                        <input type="checkbox" required checked={formData.consent} onChange={(e) => setFormData({...formData, consent: e.target.checked})} className="mt-1 w-5 h-5 text-blue-600 rounded" />
                        <span className="text-sm text-gray-700">I agree to Ascarii contacting me and have read the <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline">Privacy Policy</a>. *</span>
                      </label>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-lg font-semibold">
                      {loading ? 'Sending...' : 'Book Demo'}
                    </Button>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Modernise Your Timber Merchant?</h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Join UK timber merchants running web, counter, yard, and delivery on one connected stack.
            </p>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-12 border border-white/20">
              <p className="text-xl text-white font-semibold mb-4">Book your demo and discover:</p>
              <div className="grid md:grid-cols-2 gap-4 text-left max-w-3xl mx-auto">
                {[
                  "How to deploy the complete stack in 12-16 weeks",
                  "Real costs and ROI for your operation",
                  "Click-&-collect and B2B portal capabilities",
                  "How other merchants achieved faster service and fewer errors"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-5 h-5 text-cyan-300 flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <a href="#demo" className="inline-flex items-center justify-center rounded-xl bg-white text-blue-600 px-12 py-6 text-2xl font-bold hover:bg-blue-50 shadow-2xl transition-all hover:scale-105">
                Book Demo
              </a>
            </div>
            
            <p className="text-blue-100 text-lg mb-8">No obligations • Free consultation • Speak with a timber merchant specialist</p>
            
            <div className="flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-cyan-300" /><span className="text-lg">SAP Premier Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-cyan-300" /><span className="text-lg">AWS Advanced Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-cyan-300" /><span className="text-lg">15+ Years Experience</span></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TimberMerchantsLanding;
